import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styles from './Components.module.css';

import { ResourceCard, PollCard } from '@GiideLearning/giide-feedcards-v3';

import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';

import {
  Modal,
  Dropdown,
  Button,
  Tooltip,
  Flex,
  Skeleton,
  Card,
  DescriptionsProps,
  Descriptions,
  MenuProps,
  ConfigProvider,
} from 'antd';

import {
  DotChartOutlined,
  CaretDownOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import { useSelector, useDispatch, batch } from 'react-redux';
import { IState } from '../graphql/types';
import { setAnalyticsID, setAnalyticsData } from '../actions';
import useAnalytics from '../hooks/useQuery/useAnalytics';
import useGiide from '../hooks/useQuery/useGiide';

import { colors, modalTheme } from '../theme';

const AnalyticsModal: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const chartRef = React.useRef(null);

  const analyticsID = useSelector<IState, string | undefined>(
    state => state.analyticsID
  );

  const [period, setPeriod] = useState<number>(30);
  const [grouping, setGrouping] = useState<string>('day');
  const [timestamp, setTimestamp] = useState<string>('');

  const { data: giideData } = useGiide(analyticsID ?? '', true);

  const {
    refetch,
    loading,
    data: analyticsData,
    loadingPollData,
    pollData,
  } = useAnalytics(period, grouping, analyticsID);

  const totalPollQuizResponses = useMemo(() => {
    if (!pollData) return 0;

    return pollData.reduce((memo, item) => {
      return item?.totalResponses !== undefined
        ? memo + item?.totalResponses
        : memo;
    }, 0);
  }, [pollData]);

  // create timestamp for latest data refresh
  useEffect(() => {
    const timestamp = new Date(
      new Date().setDate(new Date().getDate())
    ).toString();
    const split = timestamp.split(' ');
    const time = split[4].split(':');
    let standardHours;
    if (Number(time[0]) > 12) {
      standardHours = Number(time[0]) - 12;
    } else standardHours = false;
    const standard = `${standardHours ? standardHours : time[0]}:${time[1]}:${
      time[2]
    } ${standardHours ? 'PM' : 'AM'}`;
    setTimestamp(`${split[0]} ${split[1]} ${split[2]} ${split[3]} ${standard}`);
  }, [analyticsData]);

  const formatDate = useCallback(
    (string: string) => {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const dateParts = string.split(' ')[0].split('-');
      if (grouping === 'day' || grouping === 'week') {
        return `${months[Number(dateParts[1]) - 1]} ${dateParts[2]}`;
      } else if (grouping === 'month') {
        return `${months[Number(dateParts[1]) - 1]} ${dateParts[0]}`;
      } else if (grouping === 'year') {
        return `${dateParts[0]}`;
      }
    },
    [grouping]
  );

  const formatData = useCallback(
    data => {
      if (!data) return;
      return data.map(event => {
        let starts = 0;
        let completions = 0;
        if (event.eventName === 'startChapter') {
          starts = event.count;
          data.forEach(e => {
            if (
              e.timestamp === event.timestamp &&
              e.eventName === 'endChapter'
            ) {
              completions = e.count;
            }
          });
        }
        return {
          starts,
          completions,
          x: formatDate(event.timestamp),
        };
      });
    },
    [formatDate]
  );

  const formatChartLabels = useCallback(() => {
    let labels: string[] = [];
    for (let i = period; i >= 1; i--) {
      const priorDate = new Date(new Date().setDate(new Date().getDate() - i));
      const dateParts = priorDate.toString().split(' ');
      if (grouping === 'day') {
        labels.push(`${dateParts[1]} ${dateParts[2]}`);
      } else if (grouping === 'week') {
        if (dateParts.includes('Mon')) {
          labels.push(`${dateParts[1]} ${dateParts[2]}`);
        }
      } else if (grouping === 'month') {
        if (!labels.includes(`${dateParts[1]} ${dateParts[3]}`)) {
          labels.push(`${dateParts[1]} ${dateParts[3]}`);
        }
      } else if (grouping === 'year' && !labels.includes(dateParts[3])) {
        labels.push(dateParts[3]);
      }
    }
    return labels;
  }, [period, grouping]);

  const getTotalFeedcardViews = (feedcardId: string) =>
    analyticsData!
      .details!.map(d => {
        let views = 0;
        if (d?.feedCardId === feedcardId && d?.count) {
          if (Number(d.count) !== 0) views += Number(d.count);
        }
        return views;
      })
      .reduce((partial, a) => partial + a, 0);

  const getTotalEventCount = (event: string) =>
    analyticsData!
      .details!.map(d => (d?.eventName === event ? d.count : 0))
      .reduce((partial, a) => partial! + a!, 0);

  // move to utils if we use again somewhere else
  const truncate = (string: string, maxLength: number) => {
    if (!string || !string.length) return;
    if (string.length <= maxLength) return string;
    const trimmed = string.substr(0, string.lastIndexOf(' ', maxLength));
    return `${trimmed}...`;
  };

  const totalPlaysData: DescriptionsProps['items'] = [
    {
      key: '1',
      label: 'Total Starts',
      children: analyticsData && getTotalEventCount('startChapter'),
    },
    {
      key: '2',
      label: 'Total Completions',
      children: analyticsData && getTotalEventCount('endChapter'),
    },
  ];

  const timePeriodMenuItems: MenuProps['items'] = useMemo(() => {
    return [
      {
        key: '30',
        label: (
          <div
            onClick={() => {
              setPeriod(30);
              if (grouping === 'year') setGrouping('day');
            }}
          >
            Last 30 Days
          </div>
        ),
      },
      {
        key: '90',
        label: (
          <div
            onClick={() => {
              setPeriod(90);
              if (grouping === 'year') setGrouping('month');
            }}
          >
            Last 90 Days
          </div>
        ),
      },
      {
        key: '365',
        label: (
          <div
            key="365"
            onClick={() => {
              setPeriod(365);
              if (grouping === 'day') setGrouping('week');
            }}
          >
            Last 365 Days
          </div>
        ),
      },
    ];
  }, [grouping]);

  const groupByMenuItems: MenuProps['items'] = useMemo(() => {
    return [
      {
        key: 'Day',
        disabled: period === 365,
        label: 'Day',
      },
      {
        key: 'Week',
        label: 'Week',
      },
      {
        key: 'Year',
        disabled: period === 30 || period === 90,
        label: 'Year',
      },
    ];
  }, [period]);

  const onClickGroupBy = ({ key }) => {
    switch (key) {
      case 'Day':
        if (period === 365) {
          return;
        }
        setGrouping('day');
        return;
      case 'Week':
        setGrouping('week');
        return;
      case 'Year':
        if (period === 30 || period === 90) {
          return;
        }
        setGrouping('year');
        return;
    }
  };

  return (
    <ConfigProvider theme={modalTheme}>
      <Modal
        closable
        destroyOnClose
        open={!!analyticsID}
        onCancel={() => {
          setPeriod(30);
          setGrouping('day');
          batch(() => {
            dispatch(setAnalyticsData(undefined));
            dispatch(setAnalyticsID(undefined));
          });
        }}
        footer={null}
        width={850}
        style={{ top: 20 }}
        title={
          giideData ? (
            `${truncate(giideData.title, 32)}`
          ) : (
            <Skeleton.Input active style={{ height: '22px' }} />
          )
        }
      >
        <div style={{ maxHeight: '70vh', overflow: 'auto', padding: '0px' }}>
          <Card
            style={{ marginTop: '24px' }}
            headStyle={{ border: 'none' }}
            title={
              <Flex
                vertical={true}
                style={{ padding: '8px 0' }}
                className={styles.analyticsCardHeader}
              >
                <Flex align={'center'} justify={'space-between'}>
                  <div>Starts & Completions</div>
                  <Flex gap={'middle'}>
                    <Flex align={'center'} gap={'small'} justify={'flex-end'}>
                      <p>Time Period:</p>
                      <Dropdown
                        trigger={['click']}
                        menu={{ items: timePeriodMenuItems }}
                      >
                        <Button type={'text'}>
                          Last {period} Days
                          <CaretDownOutlined />
                        </Button>
                      </Dropdown>
                    </Flex>

                    <Flex align={'center'} gap={'small'}>
                      <p>Group By:</p>
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: groupByMenuItems,
                          onClick: onClickGroupBy,
                        }}
                      >
                        <Button type={'text'}>
                          {grouping.charAt(0).toUpperCase() + grouping.slice(1)}
                          <CaretDownOutlined />
                        </Button>
                      </Dropdown>
                    </Flex>
                  </Flex>
                </Flex>
                <Flex
                  justify={'space-between'}
                  align={'center'}
                  style={{ alignSelf: 'flex-end' }}
                >
                  <Descriptions
                    style={{ fontWeight: '400' }}
                    size={'small'}
                    items={totalPlaysData}
                  />
                  <Flex align={'center'} className={styles.refresh}>
                    <span>Last Updated {timestamp}</span>
                    <Tooltip title="Refresh Data" placement="bottom">
                      <Button
                        shape={'default'}
                        type={'text'}
                        onClick={() => refetch()}
                        disabled={loading}
                        icon={<SyncOutlined spin={loading} />}
                      ></Button>
                    </Tooltip>
                  </Flex>
                </Flex>
              </Flex>
            }
            className={styles.analyticsChart}
          >
            {analyticsData ? (
              <div style={{ height: '300px' }}>
                <Chart
                  type="bar"
                  ref={chartRef}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: { stacked: true },
                      y: {
                        ticks: { stepSize: 1 },
                      },
                    },
                    plugins: {
                      legend: {
                        align: 'end',
                      },
                    },
                  }}
                  data={{
                    labels: formatChartLabels(),
                    datasets: [
                      {
                        label: 'Completions',
                        data: formatData(analyticsData?.details),
                        parsing: { yAxisKey: 'completions' },
                        backgroundColor: colors.green1,
                      },
                      {
                        label: 'Starts',
                        data: formatData(analyticsData?.details),
                        parsing: { yAxisKey: 'starts' },
                        backgroundColor: colors.yellow1,
                      },
                    ],
                  }}
                />
              </div>
            ) : (
              <div style={{ width: '400px' }}>
                <Skeleton.Node
                  active={true}
                  style={{
                    width: '744px',
                    height: '300px',
                    margin: '2px 0 20px 0',
                  }}
                >
                  <DotChartOutlined
                    style={{ fontSize: 120, color: '#bfbfbf' }}
                  />
                </Skeleton.Node>
              </div>
            )}
          </Card>

          <Card
            headStyle={{ border: 'none' }}
            title={
              <Flex
                className={styles.analyticsCardHeader}
                vertical={true}
                gap={'middle'}
              >
                <div>Resource Cards</div>
                <Descriptions
                  size={'small'}
                  items={[
                    {
                      key: '1',
                      label: 'Total Resource Card Views',
                      children:
                        analyticsData && getTotalEventCount('feedCardViewLink'),
                    },
                  ]}
                />
              </Flex>
            }
            className={styles.analyticsChart}
          >
            <Skeleton loading={!analyticsData}>
              <div className={styles.analyticsFeedcards}>
                {analyticsData &&
                  analyticsData.feedCardDetails &&
                  analyticsData.feedCardDetails.length > 0 && (
                    <div className={styles.analyticsFcGrid}>
                      {analyticsData.feedCardDetails.map(card => (
                        <div
                          key={card?.feedCardIdForAnalytics}
                          className={styles.analyticsFeedCardWrapper}
                        >
                          <ResourceCard
                            iconUrl={card?.iconUrl}
                            source={card?.source}
                            title={truncate(card?.title!, 30)}
                            url={card?.url}
                            imageUrl={card?.imageUrl}
                            playerHeight={205}
                            analyticsViews={getTotalFeedcardViews(
                              card?.feedCardIdForAnalytics!
                            )}
                          />
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </Skeleton>
          </Card>
          <Card
            headStyle={{ border: 'none' }}
            title={
              <Flex
                className={styles.analyticsCardHeader}
                vertical={true}
                gap={'middle'}
              >
                <div>Poll Cards</div>
                <Descriptions
                  items={[
                    {
                      key: '1',
                      label: 'Total Poll Card Responses',
                      children: totalPollQuizResponses,
                    },
                  ]}
                />
              </Flex>
            }
          >
            {((pollData && pollData.length > 0) || loadingPollData) && (
              <>
                <Skeleton loading={loadingPollData}>
                  <div className={styles.analyticsFeedcards}>
                    {pollData && pollData.length > 0 && (
                      <div className={styles.analyticsFcGrid}>
                        {pollData.map(
                          poll =>
                            poll && (
                              <div className={styles.analyticsFeedCardWrapper}>
                                <PollCard
                                  answerDetails={{
                                    userAnswer: 0,
                                    answerOptions:
                                      poll.pollQuizAnswers?.map(answer => ({
                                        index: answer!.index,
                                        sortOrder: answer!.sortOrder,
                                        text: answer!.text,
                                        result: answer!.percentResponse,
                                      })) ?? [],
                                  }}
                                />
                                <Descriptions
                                  items={[
                                    {
                                      key: '1',
                                      label: 'Responses',
                                      children: poll.totalResponses || 0,
                                    },
                                  ]}
                                />
                              </div>
                            )
                        )}
                      </div>
                    )}
                  </div>
                </Skeleton>
              </>
            )}
          </Card>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default AnalyticsModal;
